import React, { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Bookmark, Save, Trash2, Star, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * SegmentsBar — top-of-Donors-tab pill row that lets the user pick a saved
 * "dynamic list" (segment). Also lets them save the current filter view as a new
 * segment. `currentFilters` mirrors whatever active state the parent has set
 * (stage, tags, sort_by, min_amount, q, etc.).
 */
export function SegmentsBar({ api, currentFilters, onApply, onCleared }) {
  const [segments, setSegments] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const load = async () => {
    try { const { data } = await api.get('/donor-segments'); setSegments(data); }
    catch { /* silent */ }
  };
  useEffect(() => { load(); }, [api]); // load is stable; api is the only dep we need

  const apply = (seg) => {
    setActiveId(seg.id);
    onApply(seg.filters || {});
  };
  const clear = () => {
    setActiveId(null);
    onCleared();
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Give the segment a name'); return; }
    setBusy(true);
    try {
      await api.post('/donor-segments', { name, filters: currentFilters, description });
      toast.success(`Saved “${name}”`);
      setSaveOpen(false); setName(''); setDescription(''); load();
    } catch { toast.error('Save failed'); }
    setBusy(false);
  };

  const toggleFav = async (seg) => {
    try {
      await api.put(`/donor-segments/${seg.id}`, { is_favorite: !seg.is_favorite });
      load();
    } catch { toast.error('Failed'); }
  };
  const remove = async (seg) => {
    if (!window.confirm(`Delete segment “${seg.name}”?`)) return;
    try { await api.delete(`/donor-segments/${seg.id}`); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="segments-bar">
      <span className="text-xs text-gray-500 uppercase flex items-center gap-1">
        <Bookmark className="h-3 w-3" /> Segments
      </span>
      {segments.length === 0 && <span className="text-xs text-gray-400 italic">none saved yet</span>}
      {segments.map(s => (
        <div key={s.id} className={`inline-flex items-center gap-1 border rounded-full pl-2 pr-1 py-0.5 text-xs
                                    ${activeId === s.id ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-gray-700 border-gray-300'}`}
             data-testid={`segment-chip-${s.id}`}>
          <button onClick={() => apply(s)} className="flex items-center gap-1" title={s.description || s.name}>
            {s.is_favorite && <Star className="h-2.5 w-2.5 fill-current" />}
            {s.name}
          </button>
          <button onClick={() => toggleFav(s)} className="ml-0.5 opacity-60 hover:opacity-100" title="Toggle favorite">
            <Star className={`h-2.5 w-2.5 ${s.is_favorite ? 'fill-current' : ''}`} />
          </button>
          <button onClick={() => remove(s)} className="opacity-60 hover:opacity-100 hover:text-red-400" title="Delete">
            <Trash2 className="h-2.5 w-2.5" />
          </button>
        </div>
      ))}
      {activeId && (
        <button onClick={clear} className="text-xs text-red-600 hover:underline"
                data-testid="segment-clear">
          <X className="h-3 w-3 inline" /> exit segment
        </button>
      )}
      <Button size="sm" variant="outline" className="ml-auto" onClick={() => setSaveOpen(true)}
              data-testid="segments-save-btn">
        <Save className="h-3 w-3 mr-1" /> Save current view
      </Button>

      <Dialog open={saveOpen} onOpenChange={o => !o && setSaveOpen(false)}>
        <DialogContent data-testid="save-segment-dialog">
          <DialogHeader><DialogTitle>Save current filter as segment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., FY26 major gift prospects"
                     data-testid="save-segment-name" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={description} onChange={e => setDescription(e.target.value)}
                     data-testid="save-segment-description" />
            </div>
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
              Will save: <code>{JSON.stringify(currentFilters)}</code>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="save-segment-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save segment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
